/**
 * Repeatable instance edit form body — StepForm card with fill-count header (#1154).
 * Mirrors {@link RackEditFormContent} single-card layout.
 */

import React from 'react';
import { Platform, StyleSheet } from 'react-native';

import { spacing } from '../../theme';
import { subsectionHeaderMetricsProps } from '../../utils/subsectionHeaderMetrics';
import { FormStackColumnsOnWebProvider, StepForm } from '../forms';

import type { SavedAttachment } from '../../services/attachmentService';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';
import type { SelectedFile } from '../forms/FileUpload';

const EDITOR_SUBSECTION_PERSIST = {
  persistSectionExpanded: true,
} as const;

export interface RepeatableInstanceEditFormContentProps {
  title: string;
  titleSubtitle?: string;
  sectionId: string;
  requestId?: string;
  requestStage?: string;
  headerValues: Record<string, unknown>;
  metricFields: readonly SubsectionFieldDescriptor[];
  disabled?: boolean;
  attachments?: SavedAttachment[];
  onAttachFiles?: () => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: SelectedFile[];
  onFilesSelected?: (files: SelectedFile[]) => void;
  isOffline?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  children: React.ReactNode;
  testID?: string;
}

export const RepeatableInstanceEditFormContent: React.FC<
  RepeatableInstanceEditFormContentProps
> = ({
  title,
  titleSubtitle,
  sectionId,
  requestId,
  requestStage = 'DRAFT',
  headerValues,
  metricFields,
  disabled = false,
  attachments = [],
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = [],
  onFilesSelected,
  isOffline = false,
  onCaptionChange,
  pendingCaptionEdits,
  children,
  testID = 'repeatable-instance-edit-form',
}) => {
  const metrics = subsectionHeaderMetricsProps(metricFields, headerValues);

  return (
    <FormStackColumnsOnWebProvider enabled={Platform.OS === 'web'}>
      <StepForm
        title={title}
        titleSubtitle={titleSubtitle}
        defaultSectionExpanded
        {...EDITOR_SUBSECTION_PERSIST}
        {...metrics}
        sectionId={sectionId}
        requestId={requestId}
        requestStage={requestStage}
        allowAttachments
        attachments={attachments}
        onAttachFiles={onAttachFiles}
        onDeleteAttachment={onDeleteAttachment}
        selectedFiles={selectedFiles}
        onFilesSelected={onFilesSelected}
        isOffline={isOffline}
        onCaptionChange={onCaptionChange}
        pendingCaptionEdits={pendingCaptionEdits}
        isEditing={!disabled}
        disabled={disabled}
        containerStyle={styles.sectionCard}
        testID={testID}
      >
        {children}
      </StepForm>
    </FormStackColumnsOnWebProvider>
  );
};

const styles = StyleSheet.create({
  sectionCard: {
    marginBottom: spacing.md,
  },
});
